var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { bindable } from "aurelia-templating";
import { transient } from "aurelia-dependency-injection";
import { autoinject, noView } from "aurelia-framework";
import { StringUtilities } from "../../utilities/strings";
import { ImageMode } from "./image-mode";
import { computedFrom } from "aurelia-binding";
/** Narrow unknown values to File|Blob without relying on instanceof across unions */
function isFileLike(x) {
    // Both File and Blob have 'size' and 'type' properties
    return typeof x === 'object' && x !== null && ('size' in x) && ('type' in x);
}
var ImageDownloaderCustomAttribute = /** @class */ (function () {
    function ImageDownloaderCustomAttribute(element) {
        var _this = this;
        this.element = element;
        this.img = new Image();
        this._isError = false;
        this.isDataURLRegex = /^\s*data:([a-z]+\/[a-z0-9-+.]+(;[a-z-]+=[a-z0-9-]+)?)?(;base64)?,([a-z0-9!$&',()*+;=\-._~:@\/?%\s]*)\s*$/i;
        this.base = window["blob"];
        this.alwaysFit = false;
        this.setImageSource = function (element, url) {
            if (element.tagName === "IMG" && element instanceof HTMLImageElement) {
                element.src = url || '';
            }
            else {
                element.style.backgroundImage = url ? "url('".concat(url, "')") : '';
            }
            _this._isError = url === _this.errorUrl || !url;
        };
        this.getImageSource = function (element) {
            if (element.tagName === "IMG" && element instanceof HTMLImageElement) {
                return element.src;
            }
            else {
                return element.style.backgroundImage;
            }
        };
        this.getImageFormat = function (element, url) {
            if (element.tagName === "IMG") {
                return url;
            }
            else {
                return "url('".concat(url, "')");
            }
        };
    }
    Object.defineProperty(ImageDownloaderCustomAttribute.prototype, "isError", {
        get: function () {
            return this._isError;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(ImageDownloaderCustomAttribute.prototype, "dataUrl", {
        get: function () {
            return this._dataUrl ? this._dataUrl : typeof this.url === "string" ? this.url : null;
        },
        enumerable: false,
        configurable: true
    });
    ImageDownloaderCustomAttribute.prototype.baseChanged = function () {
        this.emptyImageSource(); // cancel the previous loading
        this.bind();
    };
    ImageDownloaderCustomAttribute.prototype.urlChanged = function () {
        this.emptyImageSource(); // cancel the previous loading
        if (this.url) {
            this.bind();
        }
        else {
            this.setImageSource(this.element, this.errorUrl);
        }
    };
    ImageDownloaderCustomAttribute.prototype.bind = function () {
        var _this = this;
        this._dataUrl = null;
        var loadImage = function (url, errorUrl) { return _this.loadImage(url, errorUrl); };
        var errorUrl = this.errorUrl;
        var element = this.element;
        if (typeof this.url === "string") {
            var urlDecoded = '';
            if (!this.isDataURLRegex.test(this.url)) {
                urlDecoded = this.decodeUrl(this.url);
            }
            else {
                urlDecoded = this.url;
            }
            if (this.getImageSource(element) !== this.getImageFormat(element, urlDecoded)) {
                this.loading = true;
                loadImage(urlDecoded, errorUrl);
            }
            else {
                this.loading = false;
            }
            return;
        }
        // File-like branches (File/Blob or arrays of those, or FileList)
        var u = this.url;
        var firstArrayItem = Array.isArray(u) && u.length > 0 ? u[0] : undefined;
        var hasFileInput = (u instanceof FileList) ||
            isFileLike(u) ||
            (Array.isArray(u) && isFileLike(firstArrayItem));
        if (hasFileInput) {
            this.loading = true;
            var reader_1 = new FileReader();
            reader_1.onload = function () {
                var dataURL = reader_1.result;
                _this._dataUrl = dataURL;
                loadImage(dataURL, errorUrl);
            };
            var file = null;
            if (u instanceof FileList) {
                file = u.item(0);
            }
            else if (isFileLike(u)) {
                file = u;
            }
            else if (Array.isArray(u) && isFileLike(firstArrayItem)) {
                file = firstArrayItem;
            }
            if (file) {
                reader_1.readAsDataURL(file);
            }
            else {
                // No usable file - fall back to error image
                element.classList.add("landscape");
                this.setImageSource(element, errorUrl);
                this.loading = false;
            }
            return;
        }
        // Fallback
        element.classList.add("landscape");
        this.setImageSource(element, errorUrl);
    };
    ImageDownloaderCustomAttribute.prototype.loadImage = function (url, errorUrl) {
        var _this = this;
        this.element.classList.remove('not-image');
        this.setImageSource(this.img, "");
        try {
            var loaded_1 = false;
            this.img.onload = function () {
                loaded_1 = true;
                _this.aspectRatio = _this.img.width / _this.img.height;
                var ratio = _this.img.width / _this.img.height;
                var rect = _this.element ? _this.element.getBoundingClientRect() : null;
                var clientHeight = rect ? rect.height : null;
                var clientWidth = rect ? rect.width : null;
                var landscape = ImageMode.Landscape;
                var portrait = ImageMode.Portrait;
                var mode = landscape;
                if (_this.alwaysFit) {
                    mode = ((clientHeight ? clientHeight : _this.img.width) > _this.img.height) ? landscape : portrait;
                    if (mode === landscape && clientWidth && ((clientWidth / ratio) > clientHeight)) {
                        mode = portrait;
                    }
                }
                else {
                    mode = (_this.img.width > _this.img.height) ? landscape : portrait;
                }
                _this.element.classList.add(mode);
                _this.setImageSource(_this.element, url);
                _this.loading = false;
            };
            this.img.onerror = function () {
                if (!loaded_1) {
                    _this.element.classList.add("landscape");
                    _this.setImageSource(_this.element || _this.img, errorUrl);
                    _this.loading = false;
                    _this.element.classList.add('not-image');
                }
            };
            this.img.src = url;
        }
        catch (_a) {
            this.loading = false;
        }
    };
    ImageDownloaderCustomAttribute.prototype.decodeUrl = function (rawUrl) {
        var url = decodeURIComponent(rawUrl);
        url = url.replace(new RegExp(StringUtilities.escapeRegExp("\\"), "g"), "/");
        var isExternal = url.indexOf("http://") === 0 || url.indexOf("https://") === 0;
        if (!isExternal && !StringUtilities.startsWith(url, "/")) {
            url = "/" + url;
        }
        if (this.base && !StringUtilities.startsWith(url, this.base)) {
            if (StringUtilities.endsWith(this.base, "/") && StringUtilities.startsWith(url, "/")) {
                url = url.substr(1, url.length);
            }
            url = this.base + url;
        }
        return url;
    };
    ImageDownloaderCustomAttribute.prototype.emptyImageSource = function () {
        this.img.onload = null;
        this.img.onerror = null;
        // HTMLImageElement.src is a string; set to empty string to clear safely
        this.img.src = '';
    };
    __decorate([
        computedFrom('_isError'),
        __metadata("design:type", Object),
        __metadata("design:paramtypes", [])
    ], ImageDownloaderCustomAttribute.prototype, "isError", null);
    __decorate([
        bindable,
        __metadata("design:type", Object)
    ], ImageDownloaderCustomAttribute.prototype, "url", void 0);
    __decorate([
        bindable,
        __metadata("design:type", String)
    ], ImageDownloaderCustomAttribute.prototype, "base", void 0);
    __decorate([
        bindable,
        __metadata("design:type", Boolean)
    ], ImageDownloaderCustomAttribute.prototype, "alwaysFit", void 0);
    __decorate([
        bindable,
        __metadata("design:type", String)
    ], ImageDownloaderCustomAttribute.prototype, "errorUrl", void 0);
    __decorate([
        bindable,
        __metadata("design:type", Boolean)
    ], ImageDownloaderCustomAttribute.prototype, "loading", void 0);
    __decorate([
        bindable,
        __metadata("design:type", Number)
    ], ImageDownloaderCustomAttribute.prototype, "aspectRatio", void 0);
    ImageDownloaderCustomAttribute = __decorate([
        noView(),
        autoinject(),
        transient(),
        __metadata("design:paramtypes", [Element])
    ], ImageDownloaderCustomAttribute);
    return ImageDownloaderCustomAttribute;
}());
export { ImageDownloaderCustomAttribute };
